export interface PersonalizationTemplate {
  id: string;
  name: string;
  description: string;
  category: 'role' | 'industry';
  icon: string;
  data: {
    job_role?: string;
    preferred_decision_areas?: string[];
    team_size?: string;
    company_phase?: string;
    decision_style?: string[];
    communication_preference?: string;
    detail_level?: string;
    current_priorities?: string[];
  };
}

export const PERSONALIZATION_TEMPLATES: PersonalizationTemplate[] = [
  // Role-based Templates
  {
    id: 'founder-early-stage',
    name: 'Gründer (Early Stage)',
    description: 'Fokus auf Produktentwicklung, First Customers & Fundraising',
    category: 'role',
    icon: '🚀',
    data: {
      job_role: 'Gründer / CEO',
      preferred_decision_areas: ['strategy', 'product', 'growth'],
      team_size: '1-10',
      company_phase: 'founding',
      decision_style: ['pragmatic', 'data-driven'],
      communication_preference: 'informal',
      detail_level: 'concise',
      current_priorities: ['find-focus', 'improve-product', 'accelerate-growth'],
    },
  },
  {
    id: 'founder-growth',
    name: 'Gründer (Growth Stage)',
    description: 'Fokus auf Skalierung, Team-Aufbau & Prozesse',
    category: 'role',
    icon: '📈',
    data: {
      job_role: 'Gründer / CEO',
      preferred_decision_areas: ['strategy', 'growth', 'team'],
      team_size: '11-50',
      company_phase: 'scaling',
      decision_style: ['data-driven', 'collaborative'],
      communication_preference: 'informal',
      detail_level: 'adaptive',
      current_priorities: ['scale-team', 'accelerate-growth', 'optimize-processes'],
    },
  },
  {
    id: 'ceo-enterprise',
    name: 'CEO (Enterprise)',
    description: 'Fokus auf strategische Weichenstellung & Organisationsentwicklung',
    category: 'role',
    icon: '🏢',
    data: {
      job_role: 'Führungskraft / Abteilungsleitung',
      preferred_decision_areas: ['strategy', 'team', 'operations'],
      team_size: '200+',
      company_phase: 'established',
      decision_style: ['data-driven', 'collaborative'],
      communication_preference: 'formal',
      detail_level: 'detailed',
      current_priorities: ['optimize-processes', 'reduce-costs', 'find-focus'],
    },
  },
  {
    id: 'product-lead',
    name: 'Product Lead',
    description: 'Fokus auf Produktstrategie, Roadmap & User Experience',
    category: 'role',
    icon: '🎯',
    data: {
      job_role: 'Produkt / Strategie',
      preferred_decision_areas: ['product', 'strategy', 'growth'],
      team_size: '11-50',
      company_phase: 'growth',
      decision_style: ['data-driven', 'intuitive'],
      communication_preference: 'informal',
      detail_level: 'detailed',
      current_priorities: ['improve-product', 'find-focus', 'accelerate-growth'],
    },
  },
  {
    id: 'head-of-people',
    name: 'Head of People',
    description: 'Fokus auf Kultur, Recruiting & Organisationsdesign',
    category: 'role',
    icon: '👥',
    data: {
      job_role: 'HR / People & Culture',
      preferred_decision_areas: ['team', 'strategy', 'operations'],
      team_size: '51-200',
      company_phase: 'scaling',
      decision_style: ['collaborative', 'intuitive'],
      communication_preference: 'automatic',
      detail_level: 'adaptive',
      current_priorities: ['scale-team', 'optimize-processes', 'find-focus'],
    },
  },
  {
    id: 'department-lead',
    name: 'Abteilungsleitung',
    description: 'Fokus auf Team-Führung, Prozesse & Abteilungsziele',
    category: 'role',
    icon: '📊',
    data: {
      job_role: 'Führungskraft / Abteilungsleitung',
      preferred_decision_areas: ['operations', 'team', 'strategy'],
      team_size: '51-200',
      company_phase: 'scaling',
      decision_style: ['data-driven', 'collaborative'],
      communication_preference: 'automatic',
      detail_level: 'detailed',
      current_priorities: ['optimize-processes', 'scale-team', 'reduce-costs'],
    },
  },
  
  // Industry-based Templates
  {
    id: 'saas-b2b',
    name: 'SaaS B2B',
    description: 'Typisch: PLG-Motion, Expansion-Revenue, Churn-Management',
    category: 'industry',
    icon: '💻',
    data: {
      preferred_decision_areas: ['product', 'growth', 'strategy'],
      company_phase: 'growth',
      decision_style: ['data-driven', 'pragmatic'],
      detail_level: 'detailed',
      current_priorities: ['accelerate-growth', 'improve-product', 'reduce-costs'],
    },
  },
  {
    id: 'marketplace',
    name: 'Marketplace / Platform',
    description: 'Typisch: Two-sided Growth, Liquidity, Network Effects',
    category: 'industry',
    icon: '🛒',
    data: {
      preferred_decision_areas: ['growth', 'strategy', 'product'],
      company_phase: 'growth',
      decision_style: ['data-driven', 'pragmatic'],
      detail_level: 'detailed',
      current_priorities: ['accelerate-growth', 'find-focus', 'scale-team'],
    },
  },
  {
    id: 'ecommerce',
    name: 'E-Commerce / DTC',
    description: 'Typisch: CAC/LTV-Optimierung, Conversion, Supply Chain',
    category: 'industry',
    icon: '🛍️',
    data: {
      preferred_decision_areas: ['growth', 'operations', 'finance'],
      company_phase: 'scaling',
      decision_style: ['data-driven', 'pragmatic'],
      detail_level: 'concise',
      current_priorities: ['accelerate-growth', 'reduce-costs', 'optimize-processes'],
    },
  },
  {
    id: 'fintech',
    name: 'FinTech',
    description: 'Typisch: Compliance, Trust & Security, Unit Economics',
    category: 'industry',
    icon: '💳',
    data: {
      preferred_decision_areas: ['strategy', 'operations', 'growth'],
      company_phase: 'growth',
      decision_style: ['data-driven', 'collaborative'],
      communication_preference: 'formal',
      detail_level: 'detailed',
      current_priorities: ['accelerate-growth', 'optimize-processes', 'find-focus'],
    },
  },
  {
    id: 'healthtech',
    name: 'HealthTech',
    description: 'Typisch: Regulierung, Clinical Validation, B2B2C-Sales',
    category: 'industry',
    icon: '🏥',
    data: {
      preferred_decision_areas: ['strategy', 'product', 'operations'],
      company_phase: 'growth',
      decision_style: ['data-driven', 'collaborative'],
      communication_preference: 'formal',
      detail_level: 'detailed',
      current_priorities: ['improve-product', 'find-focus', 'optimize-processes'],
    },
  },
  {
    id: 'enterprise-saas',
    name: 'Enterprise SaaS',
    description: 'Typisch: Long Sales Cycles, Implementation, Customer Success',
    category: 'industry',
    icon: '🏛️',
    data: {
      preferred_decision_areas: ['strategy', 'operations', 'team'],
      company_phase: 'scaling',
      decision_style: ['collaborative', 'data-driven'],
      communication_preference: 'formal',
      detail_level: 'detailed',
      current_priorities: ['accelerate-growth', 'optimize-processes', 'scale-team'],
    },
  },
  {
    id: 'agency-consulting',
    name: 'Agentur / Beratung',
    description: 'Typisch: Projektgeschäft, Utilization, Client Retention',
    category: 'industry',
    icon: '💼',
    data: {
      preferred_decision_areas: ['operations', 'team', 'growth'],
      team_size: '11-50',
      company_phase: 'established',
      decision_style: ['collaborative', 'intuitive'],
      detail_level: 'adaptive',
      current_priorities: ['optimize-processes', 'scale-team', 'reduce-costs'],
    },
  },
];

export const getTemplatesByCategory = (category: 'role' | 'industry') => {
  return PERSONALIZATION_TEMPLATES.filter(t => t.category === category);
};

export const getTemplateById = (id: string) => {
  return PERSONALIZATION_TEMPLATES.find(t => t.id === id);
};